import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPlatform, type InstagramAccount } from '@mg/shared';
import { Check } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { InstagramIcon } from './brand-icons';
import { PLATFORM_META } from './platforms';

/**
 * "Choose your Instagram account" picker, shown after an Instagram-via-Facebook
 * OAuth round-trip. Lists the Instagram Business accounts linked to the member's
 * Facebook Pages and connects the chosen one.
 */
export function InstagramAccountPickerModal({
  open,
  tenantKey,
  connectionId,
  onClose,
  onConnected,
  onNoAccounts,
}: {
  open: boolean;
  tenantKey: string;
  connectionId: string | null;
  onClose: () => void;
  onConnected: () => void;
  /**
   * Called (once per opening) when the list comes back empty: no Instagram professional account is
   * linked to a Page the member manages. The caller closes the picker and says so itself; until then
   * the picker keeps showing its loading state, so nothing flashes. Without it, the picker says so.
   */
  onNoAccounts?: () => void;
}) {
  const queryClient = useQueryClient();

  const accounts = useQuery({
    queryKey: ['instagram-accounts', tenantKey, connectionId],
    queryFn: ({ signal }) => api.instagramFbAccounts(connectionId as string, signal),
    enabled: open && connectionId != null,
    staleTime: 0,
    gcTime: 0,
  });

  // Only an answer fetched for THIS opening: a list left from an earlier opening is refetched first
  // (staleTime 0), and the caller closes the picker in the same handler, so it fires once.
  const noAccounts =
    open &&
    connectionId != null &&
    onNoAccounts !== undefined &&
    accounts.isSuccess &&
    !accounts.isFetching &&
    accounts.data.length === 0;
  useEffect(() => {
    if (noAccounts) onNoAccounts?.();
  }, [noAccounts, onNoAccounts]);

  const select = useMutation({
    mutationFn: (igUserId: string) =>
      api.instagramFbSelectAccount(connectionId as string, igUserId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['social-connections', tenantKey] });
      onConnected();
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose your Instagram account"
      footer={
        <Button variant="secondary" onClick={onClose} disabled={select.isPending}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">
          Pick the Instagram account you&apos;d like to connect. We&apos;ll monitor its activity and
          let you publish to it.
        </p>

        {accounts.isLoading ? (
          <LoadingState label="Loading your Instagram accounts…" />
        ) : accounts.isError ? (
          <ErrorState
            message={
              accounts.error instanceof ApiRequestError
                ? accounts.error.message
                : 'Could not load your Instagram accounts.'
            }
            onRetry={() => void accounts.refetch()}
          />
        ) : (accounts.data ?? []).length === 0 ? (
          // With onNoAccounts the caller closes the picker and says so: keep "loading" until then.
          onNoAccounts ? (
            <LoadingState label="Loading your Instagram accounts…" />
          ) : (
            <EmptyState
              title="No Instagram accounts found"
              description="We didn't find an Instagram Business account linked to your Facebook Pages. Link your Instagram professional account to a Page in Meta Business settings, then reconnect."
            />
          )
        ) : (
          <ul className="space-y-2">
            {(accounts.data ?? []).map((acct) => (
              <AccountRow
                key={acct.id}
                account={acct}
                selecting={select.isPending && select.variables === acct.id}
                disabled={select.isPending}
                onSelect={() => select.mutate(acct.id)}
              />
            ))}
          </ul>
        )}

        {select.isError && (
          <p className="text-sm text-red-600 dark:text-error-dark">
            {select.error instanceof ApiRequestError
              ? select.error.message
              : 'Could not connect that account. Please try again.'}
          </p>
        )}
      </div>
    </Modal>
  );
}

function AccountRow({
  account,
  selecting,
  disabled,
  onSelect,
}: {
  account: InstagramAccount;
  selecting: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        disabled={disabled}
        className="focus-ring flex w-full items-center gap-3 rounded-xl border border-line bg-card px-3 py-2.5 text-left transition-colors hover:border-ink-ghost hover:bg-tint disabled:cursor-not-allowed disabled:opacity-60"
      >
        {account.pictureUrl ? (
          <img
            src={account.pictureUrl}
            alt=""
            width={40}
            height={40}
            className="h-10 w-10 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              PLATFORM_META[SocialPlatform.INSTAGRAM].tile,
            )}
          >
            <InstagramIcon className="h-5 w-5" />
          </span>
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">
            @{account.username}
          </span>
          <span className="block truncate text-xs text-ink-muted">Instagram Business account</span>
        </span>
        {selecting ? (
          <span className="text-xs font-medium text-action">Connecting…</span>
        ) : (
          <Check className="h-5 w-5 shrink-0 text-ink-ghost" aria-hidden />
        )}
      </button>
    </li>
  );
}
